import { githubBesturing } from "../besturing/githubBesturing"
import { googleBesturing } from "../besturing/googleBesturing"

export interface Foto {
    url: string
    naam: string
}

export interface AlbumFoto {
    url: string
    naam: string
    detailUrl: string
}

export interface DownloadStrategy<FotoType = Foto> {
    bepaalFotos: () => Promise<Promise<FotoType>[]>
}

export interface AlbumDownloadStrategy<FotoType = AlbumFoto> {
    getAantalFotos: () => Promise<number>
    downloadFotos: (start: number, end: number) => Promise<Promise<FotoType>[]>
}

const DOWNLOAD_ERROR_PROMISE = () => Promise.reject("De foto kon niet opgehaald worden, sorry :(")
const SCAN_QR_ERROR_PROMISE = () => Promise.reject("Scan de QR code om je foto's te bekijken")

const ONBEKENDE_DOWNLOAD_STRATEGY: DownloadStrategy<any> & AlbumDownloadStrategy<any> = {
    getAantalFotos: () => DOWNLOAD_ERROR_PROMISE(),
    downloadFotos: () => DOWNLOAD_ERROR_PROMISE(),
    bepaalFotos: () => DOWNLOAD_ERROR_PROMISE(),
}

const GEEN_GEGEVENS_DOWNLOAD_STRATEGY: DownloadStrategy<any> & AlbumDownloadStrategy<any> = {
    getAantalFotos: () => SCAN_QR_ERROR_PROMISE(),
    downloadFotos: () => SCAN_QR_ERROR_PROMISE(),
    bepaalFotos: () => SCAN_QR_ERROR_PROMISE(),
}

const MAXIMUM_UNESCAPE_POGINGEN = 300

const textDecoder = new TextDecoder()

export function maakDownloadStrategy(): DownloadStrategy<Foto> {
    const urlParams = new URLSearchParams(window.location.search)
    const type = urlParams.get("type")
    if (urlParams.size === 0) {
        return GEEN_GEGEVENS_DOWNLOAD_STRATEGY
    } else {
        if (type === "gh") {
            return maakGithubStrategy(urlParams)
        } else if (type === "sheet") {
            return maakSheetStrategy(urlParams)
        } else {
            console.error("Downloadtype onbekend " + type)
            return ONBEKENDE_DOWNLOAD_STRATEGY
        }
    }
}

export function maakGalerijDownloadStrategy(): AlbumDownloadStrategy<AlbumFoto> {
    const urlParams = new URLSearchParams(window.location.search)
    const type = urlParams.get("type")
    if (urlParams.size === 0) {
        return GEEN_GEGEVENS_DOWNLOAD_STRATEGY
    } else {
        if (type === "sheet") {
            return maakSheetGalerijStrategy(urlParams)
        } else {
            console.error("Downloadtype onbekend " + type)
            return ONBEKENDE_DOWNLOAD_STRATEGY
        }
    }
}

const sleep = (seconds: number) => {
    return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), seconds * 1000)
    })
}

function getUnescapedKey(urlParameters: URLSearchParams) {
    let key = urlParameters.get("key")
    let poging = 0

    if (key === null) {
        return key
    } else {
        while (unescape(key) !== key && poging < MAXIMUM_UNESCAPE_POGINGEN) {
            // Er staan nog steeds karakters in de sleutel die unescaped kunnen worden. We gaan door tot dit niet meer mogelijk is.
            // We stoppen ook na MAXIMUM_UNESCAPE_POGINGEN
            key = unescape(key)
            poging++
        }
        return key
    }
}

function maakGithubStrategy(urlParameters: URLSearchParams): DownloadStrategy {
    return {
        bepaalFotos: () => {
            const uuidParameter = urlParameters.get("uuid")
            const fotoParameter = urlParameters.get("foto")
            const albumParameter = urlParameters.get("album")
            if (uuidParameter) {
                localStorage.setItem("token", uuidParameter)
            }
            if (albumParameter) {
                localStorage.setItem("album", albumParameter)
            }
            if (fotoParameter) {
                return Promise.resolve([
                    githubBesturing
                        .getFoto(fotoParameter)
                        .then((response: Blob) => {
                            let urlCreator = window.URL || window.webkitURL
                            const plaatje = urlCreator.createObjectURL(response)
                            return { url: plaatje, naam: fotoParameter }
                        })
                        .catch(() => DOWNLOAD_ERROR_PROMISE()),
                ])
            }
            return DOWNLOAD_ERROR_PROMISE()
        },
    }
}

function maakSheetStrategy(urlParameters: URLSearchParams): DownloadStrategy {
    const fotos = urlParameters.get("fotos")?.split(",")
    const sheetId = urlParameters.get("sheet")
    return {
        bepaalFotos: () => {
            if (fotos && sheetId) {
                return googleBesturing.zoekInSheet(sheetId, `select * WHERE ${fotos.map((foto) => `A='${foto}'`).join(` OR `)}`).then((response) =>
                    Promise.resolve(
                        response.data.map((row) => {
                            const [fotoId, , fotoType] = row
                            return verwerkSheetBestand(fotoId, fotoType, urlParameters, row.slice(3))
                        })
                    )
                )
            }

            console.error("Er ontbreken gegevens van het sheet")
            return DOWNLOAD_ERROR_PROMISE()
        },
    }
}

function maakSheetGalerijStrategy(urlParameters: URLSearchParams): AlbumDownloadStrategy<AlbumFoto> {
    const sheetId = urlParameters.get("sheet")
    let aantalFotos: number | undefined = undefined

    return {
        getAantalFotos: () => {
            if (sheetId) {
                if (aantalFotos) {
                    return Promise.resolve(aantalFotos)
                } else {
                    return googleBesturing.zoekInSheet(sheetId, `select *`).then((response) => {
                        const map = new Map<string, [string, string, string[]][]>()

                        response.data.map((row) => {
                            const [fotoId, fotoGroep, fotoType] = row
                            map.set(fotoGroep, [...(map.get(fotoGroep) ?? []), [fotoId, fotoType, row.splice(3)]])
                        })

                        aantalFotos = map.size
                        return map.size
                    })
                }
            }

            console.error("Er ontbreken gegevens van het sheet")
            return DOWNLOAD_ERROR_PROMISE()
        },
        downloadFotos: (start: number, eind: number) => {
            if (sheetId) {
                return googleBesturing.zoekInSheet(sheetId, `select *`).then((response) => {
                    const map = new Map<string, [string, string, string[]][]>()

                    response.data.map((row) => {
                        const [fotoId, fotoGroep, fotoType] = row
                        map.set(fotoGroep, [...(map.get(fotoGroep) ?? []), [fotoId, fotoType, row.splice(3)]])
                    })

                    return Promise.resolve(
                        Array.from(map.values())
                            .reverse()
                            .slice(start, eind)
                            .filter((fotosInGroep) => fotosInGroep.length > 0)
                            .map((fotosInGroep) => {
                                const [eersteFotoId, eersteFotoType, eersteFotoData] = fotosInGroep[0]

                                const detailUrl = new URL(window.location.href)
                                detailUrl.searchParams.delete("view")
                                detailUrl.searchParams.set("fotos", fotosInGroep.map(([fotoId]) => fotoId).join(","))

                                return verwerkSheetBestand(eersteFotoId, eersteFotoType, urlParameters, eersteFotoData, true).then((foto) => ({
                                    ...foto,
                                    detailUrl: detailUrl.href,
                                }))
                            })
                    )
                })
            }

            console.error("Er ontbreken gegevens van het sheet")
            return DOWNLOAD_ERROR_PROMISE()
        },
    }
}

function verwerkSheetBestand(fotoId: string, fotoType: string, urlParameters: URLSearchParams, rowData: string[], gallerij?: boolean): Promise<Foto> {
    if (fotoType === "enc_file") {
        return bepaalSheetUrlVersleuteldBestandUrl(fotoId, urlParameters, rowData, gallerij)
    } else if (fotoType === "enc_file_enc_url") {
        return bepaalSheetVersleuteldeUrlVersleuteldBestandUrl(fotoId, urlParameters, rowData, gallerij)
    } else if (fotoType === "url") {
        return bepaalSheetUrl(fotoId, rowData, gallerij)
    } else {
        console.error("Onbekend type in sheet: " + fotoType)
        return DOWNLOAD_ERROR_PROMISE()
    }
}

async function downloadEnOntsleutelBestand(sleutel: CryptoKey, ivAlsJson: string, url: string, bestandsNaam: string): Promise<Foto> {
    const initialisationValue = new Uint8Array(JSON.parse(ivAlsJson))

    const bestandResponse = await fetch(url)
    const bestandArrayBuffer = await bestandResponse.arrayBuffer()
    const bestandData = await crypto.subtle.decrypt({ name: "AES-GCM", iv: initialisationValue }, sleutel, bestandArrayBuffer)
    return { url: URL.createObjectURL(new Blob([bestandData])), naam: bestandsNaam }
}

async function bepaalSheetUrl(fotoId: string, rowData: string[], gallerij?: boolean): Promise<Foto> {
    // De data in de rij is opgebouwd urls. De gallerij moet thumbnails tonen, en die data staat achteraan in de rij.
    const url = gallerij ? rowData.at(-1)! : rowData[1]

    return {
        url,
        naam: fotoId,
    }
}

async function bepaalSheetUrlVersleuteldBestandUrl(fotoId: string, urlParams: URLSearchParams, rowData: string[], gallerij?: boolean): Promise<Foto> {
    const key = getUnescapedKey(urlParams)
    // De data in de rij is opgebouwd uit "ivAlsJson, url"-paren. De gallerij moet thumbnails tonen, en die data staat achteraan in de rij.
    const ivAlsJson = gallerij ? rowData.at(-2)! : rowData[0]
    const url = gallerij ? rowData.at(-1)! : rowData[1]

    if (key) {
        const actualKey = await crypto.subtle.importKey("jwk", JSON.parse(key), { name: "AES-GCM" }, true, ["decrypt"])

        return downloadEnOntsleutelBestand(actualKey, ivAlsJson, url, fotoId)
    } else {
        console.error("Er ontbreken gegevens voor het downloaden")
        return DOWNLOAD_ERROR_PROMISE()
    }
}

async function bepaalSheetVersleuteldeUrlVersleuteldBestandUrl(
    fotoId: string,
    urlParams: URLSearchParams,
    rowData: string[],
    gallerij?: boolean
): Promise<Foto> {
    const key = getUnescapedKey(urlParams)
    // De data in de rij is opgebouwd uit "urlIvAlsJson, bestandIvAlsJson, url"-paren. De gallerij moet thumbnails tonen, en die data staat achteraan in de rij.
    const urlIvAlsJson = gallerij ? rowData.at(-3)! : rowData[0]
    const bestandIvAlsJson = gallerij ? rowData.at(-2)! : rowData[1]
    const versleuteldeUrl = gallerij ? rowData.at(-1)! : rowData[2]

    if (key) {
        const actualKey = await crypto.subtle.importKey("jwk", JSON.parse(key), { name: "AES-GCM" }, true, ["decrypt"])
        const urlInitialisationValue = new Uint8Array(JSON.parse(urlIvAlsJson))
        const urlData = await crypto.subtle.decrypt({ name: "AES-GCM", iv: urlInitialisationValue }, actualKey, new Uint8Array(JSON.parse(versleuteldeUrl)))

        const url = textDecoder.decode(urlData)
        console.log(url)

        return downloadEnOntsleutelBestand(actualKey, bestandIvAlsJson, url, fotoId)
    } else {
        console.error("Er ontbreken gegevens voor het downloaden")
        return DOWNLOAD_ERROR_PROMISE()
    }
}
